import React, { useState } from "react";
import clientAxiosInstance from "../api/clientAxiosInstance";

const OTPLogin = ({ household_id, onAuthSuccess, error, setError }) => {
  const [step, setStep] = useState("request"); // "request" or "verify"
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [policiesAccepted, setPoliciesAccepted] = useState(false);

  const sendOTP = async () => {
    try {
      setLoading(true);
      setError("");
      
      await clientAxiosInstance.post("/client/login/email", {
        household_id: household_id
      });
      
      setOtpSent(true);
      setStep("verify");
      setCountdown(300); // 5 minutes countdown
      
      // Start countdown
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error("Error sending OTP:", error);
      const fallback =
        error.response?.status === 429
          ? "Too many attempts. Please try again later."
          : "Failed to send OTP. Please try again.";
      setError(error.response?.data?.detail || fallback);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 4) {
      setError("Please enter a valid 4-digit OTP");
      return;
    }

    if (!policiesAccepted) {
      setError("Please accept the Privacy Policy and Data Consent Policy to continue");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response = await clientAxiosInstance.post("/client/verify", {
        household_id: household_id,
        otp: otp
      });
      
      const { access_token, expires_in } = response.data;
      onAuthSuccess(access_token, expires_in);
      
    } catch (error) {
      console.error("Error verifying OTP:", error);
      const fallback =
        error.response?.status === 429
          ? "Too many attempts. Please try again later."
          : "Invalid OTP. Please try again.";
      setError(error.response?.data?.detail || fallback);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setOtp(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (step === "request") {
        sendOTP();
      } else {
        verifyOTP();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Secure Access</h1>
          <p className="text-gray-600 text-lg">Access your financial information securely</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === "request" ? (
            <>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
                Verify Your Identity
              </h2>
              <p className="text-gray-600 mb-6 text-center leading-relaxed">
                We'll send a secure verification code to your registered email address to ensure your account safety.
              </p>
              
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={sendOTP}
                disabled={loading}
                onKeyPress={handleKeyPress}
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-xl transition duration-200 text-lg"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending Code...
                  </div>
                ) : (
                  "Send Verification Code"
                )}
              </button>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">
                Enter Verification Code
              </h2>
              <p className="text-gray-600 mb-6 text-center leading-relaxed">
                Please enter the 4-digit code we sent to your email address.
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <div className="mb-6">
                <input
                  type="text"
                  value={otp}
                  onChange={handleOtpChange}
                  onKeyPress={handleKeyPress}
                  placeholder="0000"
                  className="w-full text-center text-3xl font-mono tracking-widest py-4 px-4 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none"
                  maxLength="4"
                  autoFocus
                />
              </div>

              {countdown > 0 && (
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-500">
                    Code expires in: <span className="font-mono font-semibold text-emerald-600">{formatTime(countdown)}</span>
                  </p>
                </div>
              )}

              <label className="flex items-center gap-2.5 mb-5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={policiesAccepted}
                  onChange={(e) => setPoliciesAccepted(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-emerald-500 focus:ring-2 focus:ring-emerald-500/30 cursor-pointer flex-shrink-0"
                />
                <span className="text-xs text-gray-600 leading-tight">
                  You agree to our{" "}
                  <a
                    href="https://fasttrackr.ai/client-privacy-notice"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 underline hover:text-emerald-700 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://fasttrackr.ai/client-data-consent-acknowledgment"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 underline hover:text-emerald-700 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Data Consent Policy
                  </a>
                </span>
              </label>

              <button
                onClick={verifyOTP}
                disabled={loading || otp.length !== 4 || !policiesAccepted}
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-xl transition duration-200 text-lg mb-4"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  "Verify Code"
                )}
              </button>

              <button
                onClick={() => {
                  setStep("request");
                  setOtp("");
                  setError("");
                  setOtpSent(false);
                  setCountdown(0);
                  setPoliciesAccepted(false);
                }}
                className="w-full text-emerald-600 hover:text-emerald-700 font-medium py-2 transition duration-200"
              >
                ← Back to Send Code
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Having trouble? Contact your financial advisor for assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPLogin;
